 
import { DEVELOPMENT_SOURCE_METRIC } from 'constants/sources';
import assertNever from 'helpers/assertNever';
import {
    COST_BASIS_TYPES, EXPENSE_CALCULATION_MODE, UNIT_OF_TIME_TYPES,
} from './constants';
import { BOESlideoutFormValues, IBackOfEnvelopePayload } from './types';

export const getValueOrNull = <T>(value: T | null | undefined): T | null => (value !== null && value !== undefined ? value : null);

export const prepareBackOfEnvelopePayload = (formikValues: BOESlideoutFormValues): IBackOfEnvelopePayload => {
    const normalizeValue = (value: number | null | undefined): number | undefined => (value !== null && value !== undefined ? value : undefined);

    const loanCalculationField = (() => {
        switch (formikValues.selectedLoanCalculationOptions?.value) {
            case DEVELOPMENT_SOURCE_METRIC.LTC:
                return { loanToCost: formikValues.contructionFinancing ? formikValues.contructionFinancing / 100 : undefined };
            case DEVELOPMENT_SOURCE_METRIC.LTV:
                return { loanToValue: formikValues.contructionFinancing ? formikValues.contructionFinancing / 100 : undefined };
            case DEVELOPMENT_SOURCE_METRIC.DEBT_YIELD:
                return { debtYield: formikValues.contructionFinancing ? formikValues.contructionFinancing / 100 : undefined };
            default:
                return {};
        }
    })();

    const expensePercentOfGpr = (() => {
        switch (formikValues.expenseCalculationMode) {
            case EXPENSE_CALCULATION_MODE.PERCENT_OF_GPR:
                return formikValues.expensePercentOfGpr ? formikValues.expensePercentOfGpr / 100 : undefined;
            case EXPENSE_CALCULATION_MODE.UNIT_COST:
                return 0;
            default:
                return assertNever(formikValues.expenseCalculationMode);
        }
    })();

    return {
        name: formikValues.name,
        dealId: formikValues.dealId,
        propertyTypeId: formikValues.propertyType.value,
        builtSf: formikValues.grossSquareFootage!,
        buildingEfficiency: formikValues.efficiency ? formikValues.efficiency / 100 : undefined,
        monthlyRentPricePerUnit:
            (
                formikValues.rentPriceSubdiv === COST_BASIS_TYPES.PER_UNIT ||
                formikValues.rentPriceSubdiv === COST_BASIS_TYPES.PER_KW ||
                formikValues.rentPriceSubdiv === COST_BASIS_TYPES.PER_MW
            )
                ? normalizeValue(formikValues.rentPrice)
                : undefined,
        monthlyRentPricePerSf: formikValues.rentPriceSubdiv === COST_BASIS_TYPES.PER_SF ? normalizeValue(formikValues.rentPrice) : undefined,
        rentPriceTime: formikValues.rentPriceTime,
        rentPriceSubdiv: formikValues.rentPriceSubdiv,
        numberOfUnits: formikValues.units!,
        monthlyExpenseCostPerUnit:
            (
                formikValues.expenseCostSubdiv === COST_BASIS_TYPES.PER_UNIT ||
                formikValues.expenseCostSubdiv === COST_BASIS_TYPES.PER_KW ||
                formikValues.expenseCostSubdiv === COST_BASIS_TYPES.PER_MW
            )
                ? normalizeValue(formikValues.expenses)
                : undefined,
        monthlyExpenseCostPerSf: formikValues.expenseCostSubdiv === COST_BASIS_TYPES.PER_SF ? normalizeValue(formikValues.expenses) : undefined,
        expenseCostTime: formikValues.expenseCostTime,
        expenseCostSubdiv: formikValues.expenseCostSubdiv,
        expenseCalculationMode: formikValues.expenseCalculationMode,
        expensePercentOfGpr,
        assumedExitCapRate: formikValues.exitCapRate ? formikValues.exitCapRate / 100 : undefined,
        marketCapRate: formikValues.marketCapRate ? formikValues.marketCapRate / 100 : undefined,
        acquisition: formikValues.acquisitionCosts!,
        hardCosts: formikValues.hardCosts ? normalizeValue(formikValues.hardCosts) : undefined,
        hardCostsSubdiv: formikValues.hardCostsSubdiv,
        softCosts: formikValues.softCosts ? normalizeValue(formikValues.softCosts) : undefined,
        softCostsSubdiv: formikValues.softCostsSubdiv,
        developerFeeRate: formikValues.developerFee ? formikValues.developerFee / 100 : undefined,
        annualPercentageRate: formikValues.apr ? formikValues.apr / 100 : undefined,
        averageLoanBalance: formikValues.avgLoanBalance ? formikValues.avgLoanBalance / 100 : undefined,
        loanTermMonths: formikValues.loanTerm!,
        loanTermTime: formikValues.loanTermTime, // Backend is multiplying it
        ...loanCalculationField,
    };
};

export const calculateBOEValues = (values: BOESlideoutFormValues) => {
    const isPerUnitType = (subdiv: string) => subdiv === COST_BASIS_TYPES.PER_UNIT ||
        subdiv === COST_BASIS_TYPES.PER_KW ||
        subdiv === COST_BASIS_TYPES.PER_MW;

    // Convert number_of_units to kW for unit conversions (units is in the unit specified by rent_price_subdiv)
    const unitsInKw = (() => {
        if (values.rentPriceSubdiv === COST_BASIS_TYPES.PER_MW) {
            return (values.units ?? 0) * 1000; // Convert MW to kW
        }
        return values.units ?? 0; // Already in kW or units
    })();

    const calculateCostWithUnitConversion = (
        amount: number | null,
        targetSubdiv: string,
        rentPriceSubdiv: string,
        units: number | null,
        defaultMultiplier: number,
        fallbackMultiplier: number,
    ) => {
        if (!isPerUnitType(targetSubdiv)) {
            // Per SF calculations
            return (amount ?? 0) * fallbackMultiplier * defaultMultiplier;
        }

        // Per unit calculations with unit conversion
        let unitsToUse = units ?? 0;

        if (rentPriceSubdiv === COST_BASIS_TYPES.PER_MW && targetSubdiv === COST_BASIS_TYPES.PER_KW) {
            unitsToUse = unitsInKw; // Use converted kW value
        } else if (rentPriceSubdiv === COST_BASIS_TYPES.PER_KW && targetSubdiv === COST_BASIS_TYPES.PER_MW) {
            unitsToUse = unitsInKw / 1000; // Convert kW to MW
        } else if (rentPriceSubdiv === COST_BASIS_TYPES.PER_MW && targetSubdiv === COST_BASIS_TYPES.PER_MW) {
            unitsToUse = units ?? 0; // Use original MW value
        } else if (rentPriceSubdiv === COST_BASIS_TYPES.PER_KW && targetSubdiv === COST_BASIS_TYPES.PER_KW) {
            unitsToUse = units ?? 0; // Use original kW value
        }

        return (amount ?? 0) * unitsToUse * defaultMultiplier;
    };

    const rentTimeMultiplier = values.rentPriceTime === UNIT_OF_TIME_TYPES.MONTHLY ? 12 : 1;
    const expensesTimeMultiplier = values.expenseCostTime === UNIT_OF_TIME_TYPES.MONTHLY ? 12 : 1;

    const netRentable = (values.grossSquareFootage ?? 0) * ((values.efficiency ?? 0) / 100);
    const sfPerUnit = values.units ? netRentable / values.units : 0;

    const grossPotentialRent = calculateCostWithUnitConversion(
        values.rentPrice,
        values.rentPriceSubdiv,
        values.rentPriceSubdiv,
        values.units,
        rentTimeMultiplier,
        netRentable,
    );

    const expenseTotal = values.expenseCalculationMode === EXPENSE_CALCULATION_MODE.PERCENT_OF_GPR
        ? grossPotentialRent * ((values.expensePercentOfGpr ?? 0) / 100)
        : calculateCostWithUnitConversion(
            values.expenses,
            values.expenseCostSubdiv,
            values.rentPriceSubdiv,
            values.units,
            expensesTimeMultiplier,
            netRentable,
        );

    const netOperatingIncome = grossPotentialRent - expenseTotal;
    const exitValuation = values.exitCapRate && values.exitCapRate !== 0 ? netOperatingIncome / ((values.exitCapRate ?? 1) / 100) : 0;

    const calculateDevCost = (
        amount: number | null,
        subdiv: string,
        units: number | null,
        grossSqFt: number | null,
    ) => {
        if (subdiv === COST_BASIS_TYPES.TOTAL) return amount ?? 0;
        if (!isPerUnitType(subdiv)) return (amount ?? 0) * (grossSqFt ?? 0);

        // Handle unit conversions for development costs
        let unitsToUse = units ?? 0;

        if (values.rentPriceSubdiv === COST_BASIS_TYPES.PER_MW && subdiv === COST_BASIS_TYPES.PER_KW) {
            unitsToUse = unitsInKw; // Use converted kW value
        } else if (values.rentPriceSubdiv === COST_BASIS_TYPES.PER_KW && subdiv === COST_BASIS_TYPES.PER_MW) {
            unitsToUse = unitsInKw / 1000; // Convert kW to MW
        } else if (values.rentPriceSubdiv === COST_BASIS_TYPES.PER_MW && subdiv === COST_BASIS_TYPES.PER_MW) {
            unitsToUse = units ?? 0; // Use original MW value
        } else if (values.rentPriceSubdiv === COST_BASIS_TYPES.PER_KW && subdiv === COST_BASIS_TYPES.PER_KW) {
            unitsToUse = units ?? 0; // Use original kW value
        }

        return (amount ?? 0) * unitsToUse;
    };
    const hardCostsTotal = calculateDevCost(
        values.hardCosts,
        values.hardCostsSubdiv,
        values.units,
        values.grossSquareFootage,
    );

    const softCostsTotal = calculateDevCost(
        values.softCosts,
        values.softCostsSubdiv,
        values.units,
        values.grossSquareFootage,
    );

    const calculateLoanAmount = (totalDevelopmentCosts: number) => {
        const constructionFinancing = values.contructionFinancing ?? 0;
        const marketCapRate = values.marketCapRate ?? 0;

        switch (values.selectedLoanCalculationOptions?.value) {
            case DEVELOPMENT_SOURCE_METRIC.LTV:
                return marketCapRate === 0 ? 0 : (netOperatingIncome / (marketCapRate / 100)) * (constructionFinancing / 100);

            case DEVELOPMENT_SOURCE_METRIC.LTC:
                return (constructionFinancing / 100) * totalDevelopmentCosts;
            case DEVELOPMENT_SOURCE_METRIC.DEBT_YIELD:
                return constructionFinancing === 0 ? 0 : netOperatingIncome / (constructionFinancing / 100);

            default:
                return 0;
        }
    };

    return {
        netRentable,
        sfPerUnit,
        grossPotentialRent,
        expenseTotal,
        netOperatingIncome,
        exitValuation,
        calculateLoanAmount,
        hardCostsTotal,
        softCostsTotal,
    };
};

export const calculateIterativeValues = (
    values: BOESlideoutFormValues,
    hardCostsTotal: number,
    softCostsTotal: number,
    netOperatingIncome: number,
    exitValuation: number,
    calculateLoanAmount: (totalDevelopmentCosts: number) => number,
) => {
    const maxIterations = 100;
    const convergenceThreshold = 0.01;
    let iterations = 0;

    let developerFeeTotal = 0;
    let prevDeveloperFeeTotal = 0;

    let interestReserveTotal = 0;
    let loanAmount = 0;
    let equityCost = 0;
    let returnOnCost = 0;
    let returnOnEquity = 0;
    let profitReturn = 0;
    let totalDevelopmentCosts = 0;

    while (iterations < maxIterations) {
        totalDevelopmentCosts = (values.acquisitionCosts ?? 0) + hardCostsTotal + softCostsTotal + developerFeeTotal + interestReserveTotal;
        developerFeeTotal = ((values.developerFee ?? 0) / 100) * totalDevelopmentCosts;

        loanAmount = calculateLoanAmount(totalDevelopmentCosts);

        const normalizedLoanTerm = values.loanTermTime === UNIT_OF_TIME_TYPES.MONTHLY ? (values.loanTerm ?? 0) / 12 : (values.loanTerm ?? 0);

        interestReserveTotal = loanAmount * ((values.apr ?? 0) / 100) * normalizedLoanTerm * ((values.avgLoanBalance ?? 0) / 100);

        equityCost = totalDevelopmentCosts - loanAmount;

        returnOnCost = totalDevelopmentCosts !== 0 ? netOperatingIncome / totalDevelopmentCosts : 0;

        profitReturn = exitValuation - totalDevelopmentCosts;

        returnOnEquity = equityCost !== 0 ? profitReturn / equityCost : 0;

        if (Math.abs(developerFeeTotal - prevDeveloperFeeTotal) < convergenceThreshold) {
            break;
        }
        prevDeveloperFeeTotal = developerFeeTotal;
        iterations++;
    }

    return {
        developerFeeTotal,
        interestReserveTotal,
        loanAmount,
        equityCost,
        returnOnCost,
        returnOnEquity,
        profitReturn,
        totalDevelopmentCosts,
    };
};
